import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    InputBase,
    Avatar,
    Pagination,
    Menu,
    MenuItem,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
    Add as AddIcon,
    Search as SearchIcon,
    GridView as GridIcon,
    ViewList as ListIcon,
    MoreVert as MoreIcon,
    VisibilityOutlined as OverviewIcon,
    PeopleOutline as PeopleIcon,
    Business as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import clientService from 'src/clientService';
import { OrganisationCreate, OrganisationResponse, ProjectAdminSummary } from 'src/api/Api';
import { useSnackbar } from 'src/view/components/SnackbarService';
import {
    ORGANISATION_AVATAR_COLOR,
    formatStatusLabel,
    getOrganisationInitials,
    getStatusColor,
} from './organisationUtils';

interface ApiErrorShape {
    detail?: { msg?: string }[];
    error?: { message?: string };
    message?: string;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
    const apiError = error as ApiErrorShape;
    return apiError?.detail?.[0]?.msg || apiError?.error?.message || apiError?.message || fallback;
};

const CreateOrganisationDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
}> = ({ open, onClose, onCreated }) => {
    const [formData, setFormData] = useState<OrganisationCreate>({
        name: '',
        domain: '',
    });
    const [loading, setLoading] = useState(false);
    const { showSnackbar, snackbarType } = useSnackbar();

    const handleClose = () => {
        if (loading) {
            return;
        }
        setFormData({ name: '', domain: '' });
        onClose();
    };

    const handleCreate = async () => {
        if (!formData.name.trim()) {
            showSnackbar({ message: 'Organisation name is required.', type: snackbarType.WARNING });
            return;
        }

        setLoading(true);
        try {
            await clientService.api.createOrganisationApiV1OrganisationsPost({
                name: formData.name.trim(),
                domain: formData.domain?.trim() || null,
            });
            showSnackbar({ message: 'Organisation created successfully.', type: snackbarType.SUCCESS });
            await onCreated();
            setFormData({ name: '', domain: '' });
            onClose();
        } catch (error) {
            showSnackbar({
                message: getErrorMessage(error, 'Failed to create organisation.'),
                type: snackbarType.ERROR,
            });
        } finally {
            setLoading(false);
        }
    };


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#0D0D0D',
                    backgroundImage: 'none',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    p: 2,
                },
            }}
        >
            <DialogTitle sx={{ color: '#fff', fontWeight: 800, fontSize: '1.5rem', pb: 1 }}>
                New Organisation
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 1, fontSize: '0.9rem' }}>
                        Create a workspace for an organisation.
                    </Typography>

                    <Box>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1, ml: 0.5 }}>
                            Organisation Name
                        </Typography>
                        <TextField
                            value={formData.name}
                            onChange={(event) => setFormData(prev => ({ ...prev, name: event.target.value }))}
                            fullWidth
                            required
                            autoComplete="off"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1, ml: 0.5 }}>
                            Domain
                        </Typography>
                        <TextField
                            value={formData.domain || ''}
                            onChange={(event) => setFormData(prev => ({ ...prev, domain: event.target.value }))}
                            fullWidth
                            autoComplete="off"
                            placeholder="example.org"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(255,255,255,0.3)',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'none', fontWeight: 600 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    disabled={loading || !formData.name.trim()}
                    sx={{
                        bgcolor: '#10B981',
                        '&:hover': { bgcolor: '#059669' },
                        color: '#000',
                        fontWeight: 800,
                        textTransform: 'none',
                        px: 4,
                        borderRadius: '10px',
                        minWidth: '120px',
                    }}
                >
                    {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


const OrganisationCard: React.FC<{
    organisation: OrganisationResponse;
    memberCount?: number;
    projectCount?: number;
}> = ({ organisation, memberCount = 0, projectCount = 0 }) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);
    const statusColor = getStatusColor(organisation.status);

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'rgba(255,255,255,0.03)',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.05)',
                p: 3,
                transition: 'all .3s cubic-bezier(0.4,0,0.2,1)',
                '&:hover': {
                    transform: 'translateY(-6px)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderColor: alpha('#10B981', 0.2),
                    boxShadow: `0 12px 24px -8px ${alpha('#000', 0.5)}`
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 3
                }}
            >
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '16px',
                            bgcolor: alpha(ORGANISATION_AVATAR_COLOR, 0.15),
                            color: ORGANISATION_AVATAR_COLOR,
                            fontWeight: 800,
                            fontSize: '1.2rem',
                            border: `1px solid ${alpha(ORGANISATION_AVATAR_COLOR, 0.3)}`
                        }}
                    >
                        {getOrganisationInitials(organisation.name)}
                    </Avatar>

                    <Box>
                        <Typography
                            sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                mb: 0.5
                            }}
                        >
                            {organisation.name}
                        </Typography>

                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.45)',
                                fontSize: '0.85rem'
                            }}
                        >
                            {organisation.domain || 'Organisation Workspace'}
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        color: 'rgba(255,255,255,0.3)'
                    }}
                >
                    <MoreIcon />
                </IconButton>

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                        sx: {
                            bgcolor: '#151515',
                            color: '#fff',
                            borderRadius: '12px',
                            border:
                                '1px solid rgba(255,255,255,0.08)'
                        }
                    }}
                >
                    <MenuItem
                        onClick={() =>
                            navigate(
                                `/admin/organisation/${organisation.org_id}`
                            )
                        }
                    >
                        View Overview
                    </MenuItem>

                    <MenuItem
                        onClick={() =>
                            navigate(
                                `/admin/organisation/${organisation.org_id}/members`
                            )
                        }
                    >
                        Manage Members
                    </MenuItem>
                </Menu>
            </Box>

            {/* Description */}
            <Typography
                sx={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '0.9rem',
                    minHeight: '48px'
                }}
            >
                Manage members, permissions, projects and organisation
                resources.
            </Typography>

            <Box
                sx={{
                    my: 3,
                    borderTop:
                        '1px solid rgba(255,255,255,0.05)'
                }}
            />

            {/* Stats */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3,1fr)',
                    gap: 2,
                    mb: 3
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.25)',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 700
                        }}
                    >
                        Members
                    </Typography>

                    <Typography
                        sx={{
                            color: '#fff',
                            fontWeight: 800,
                            mt: 0.5
                        }}
                    >
                        {memberCount}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.25)',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 700
                        }}
                    >
                        Projects
                    </Typography>

                    <Typography
                        sx={{
                            color: '#10B981',
                            fontWeight: 800,
                            mt: 0.5
                        }}
                    >
                        {projectCount}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.25)',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            fontWeight: 700
                        }}
                    >
                        Status
                    </Typography>

                    <Typography
                        sx={{
                            color: statusColor,
                            fontWeight: 800,
                            mt: 0.5
                        }}
                    >
                        {formatStatusLabel(organisation.status)}
                    </Typography>
                </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 'auto' }}>
                <Button
                    fullWidth
                    startIcon={<OverviewIcon />}
                    onClick={() =>
                        navigate(
                            `/admin/organisation/${organisation.org_id}`
                        )
                    }
                    sx={{
                        color: '#fff',
                        border:
                            '1px solid rgba(255,255,255,0.08)',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        borderRadius: '12px',
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.08)'
                        }
                    }}
                >
                    Overview
                </Button>

                <Button
                    fullWidth
                    startIcon={<PeopleIcon />}
                    onClick={() =>
                        navigate(
                            `/admin/organisation/${organisation.org_id}/members`
                        )
                    }
                    sx={{
                        color: '#10B981',
                        border: `1px solid ${alpha(
                            '#10B981',
                            0.25
                        )}`,
                        bgcolor: alpha('#10B981', 0.08),
                        borderRadius: '12px',
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': {
                            bgcolor: alpha('#10B981', 0.12)
                        }
                    }}
                >
                    Manage Members
                </Button>
            </Box>
        </Box>
    );
};

const OrganisationsPage: React.FC = () => {
    const [organisations, setOrganisations] = useState<OrganisationResponse[]>([]);
    const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
    const [projectCounts, setProjectCounts] = useState<Record<string, number>>({});
    const [totalUsers, setTotalUsers] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [page, setPage] = useState(1);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const { showSnackbar, snackbarType } = useSnackbar();

    const rowsPerPage = 6;

    const fetchOrganisations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [response, projectsResponse, usersResponse] = await Promise.all([
                clientService.api.listOrganisationsApiV1OrganisationsGet({
                    skip: 0,
                    limit: 500,
                }),
                clientService.api.getAllProjectsAdminApiV1AdminProjectsGet().catch(() => ({ data: [] })),
                clientService.api.listUsersApiV1AdminUsersGet({ page: 1, per_page: 1 }).catch(() => ({ data: { count: 0 } })),
            ]);

            const orgs = response.data.data || [];
            setOrganisations(orgs);

            if (usersResponse.data && typeof usersResponse.data.count === 'number') {
                setTotalUsers(usersResponse.data.count);
            }

            const allProjects: ProjectAdminSummary[] = Array.isArray(projectsResponse.data)
                ? (projectsResponse.data as ProjectAdminSummary[])
                : ((projectsResponse.data as { data?: ProjectAdminSummary[] })?.data || []);

            const pCounts: Record<string, number> = {};
            allProjects.forEach((p) => {
                if (p.org_id) {
                    pCounts[p.org_id] = (pCounts[p.org_id] || 0) + 1;
                }
            });
            setProjectCounts(pCounts);

            const counts: Record<string, number> = {};

            await Promise.all(
                orgs.map(async (org) => {
                    try {
                        const membersResponse =
                            await clientService.api.listOrganisationMembersApiV1OrganisationsOrgIdMembersGet(
                                org.org_id,
                                {
                                    skip: 0,
                                    limit: 500,
                                }
                            );

                        counts[org.org_id] = membersResponse.data.data?.length || 0;
                    } catch (error) {
                        counts[org.org_id] = 0;
                    }
                })
            );

            setMemberCounts(counts);
        } catch (err) {
            const errorMessage = getErrorMessage(err, 'Failed to fetch organisations.');
            setError(errorMessage);
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showSnackbar, snackbarType.ERROR]);

    useEffect(() => {
        fetchOrganisations();
    }, [fetchOrganisations]);

    const filteredOrganisations = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) {
            return organisations;
        }

        return organisations.filter((org) =>
            [org.name, org.domain || '', org.status || '']
                .join(' ')
                .toLowerCase()
                .includes(query)
        );
    }, [organisations, searchQuery]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery]);

    const paginatedOrganisations = filteredOrganisations.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const totalPages = Math.max(1, Math.ceil(filteredOrganisations.length / rowsPerPage));

    return (
        <Box
            sx={{
                px: 4,
                py: 4,
                bgcolor: '#0D0D0D',
                minHeight: '100%',
                borderRadius: '16px'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 5
                }}
            >
                <Box>
                    <Typography
                        variant="h3"
                        sx={{
                            color: '#fff',
                            fontWeight: 800,
                            mb: 1
                        }}
                    >
                        Organisations
                    </Typography>

                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.5)'
                        }}
                    >
                        Manage all organisations and their members.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{
                        bgcolor: '#10B981',
                        color: '#000',
                        fontWeight: 800,
                        textTransform: 'none',
                        px: 3,
                        py: 1.2,
                        borderRadius: '12px',
                        '&:hover': {
                            bgcolor: '#059669'
                        }
                    }}
                >
                    New Organisation
                </Button>
            </Box>

            {/* Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                        sx={{
                            p: 3,
                            bgcolor: 'rgba(255,255,255,0.03)',
                            borderRadius: '20px',
                            border:
                                '1px solid rgba(255,255,255,0.05)'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: alpha(
                                        '#10B981',
                                        0.15
                                    ),
                                    color: '#10B981'
                                }}
                            >
                                <BusinessIcon />
                            </Avatar>

                            <Box>
                                <Typography
                                    sx={{
                                        color:
                                            'rgba(255,255,255,0.5)'
                                    }}
                                >
                                    Total Organisations
                                </Typography>

                                <Typography
                                    sx={{
                                        color: '#fff',
                                        fontSize: '2rem',
                                        fontWeight: 800
                                    }}
                                >
                                    {organisations.length}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                        sx={{
                            p: 3,
                            bgcolor: 'rgba(255,255,255,0.03)',
                            borderRadius: '20px',
                            border:
                                '1px solid rgba(255,255,255,0.05)'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: alpha(
                                        '#3B82F6',
                                        0.15
                                    ),
                                    color: '#3B82F6'
                                }}
                            >
                                <PeopleIcon />
                            </Avatar>

                            <Box>
                                <Typography
                                    sx={{
                                        color:
                                            'rgba(255,255,255,0.5)'
                                    }}
                                >
                                    Total Users
                                </Typography>

                                <Typography
                                    sx={{
                                        color: '#fff',
                                        fontSize: '2rem',
                                        fontWeight: 800
                                    }}
                                >
                                    {totalUsers}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Filters */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    mb: 4
                }}
            >
                <Box
                    sx={{
                        flexGrow: 1,
                        maxWidth: 420,
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        height: 48,
                        bgcolor: 'rgba(255,255,255,0.03)',
                        border:
                            '1px solid rgba(255,255,255,0.05)',
                        borderRadius: '12px'
                    }}
                >
                    <SearchIcon
                        sx={{
                            color: 'rgba(255,255,255,0.3)',
                            mr: 1
                        }}
                    />

                    <InputBase
                        fullWidth
                        placeholder="Search organisations..."
                        value={searchQuery}
                        onChange={(e) =>
                            setSearchQuery(e.target.value)
                        }
                        sx={{ color: '#fff' }}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        borderRadius: '10px',
                        p: 0.5
                    }}
                >
                    <IconButton
                        onClick={() => setViewMode('grid')}
                        sx={{
                            color:
                                viewMode === 'grid'
                                    ? '#10B981'
                                    : 'rgba(255,255,255,0.3)'
                        }}
                    >
                        <GridIcon />
                    </IconButton>

                    <IconButton
                        onClick={() => setViewMode('list')}
                        sx={{
                            color:
                                viewMode === 'list'
                                    ? '#10B981'
                                    : 'rgba(255,255,255,0.3)'
                        }}
                    >
                        <ListIcon />
                    </IconButton>
                </Box>
            </Box>

            {loading && organisations.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress sx={{ color: '#10B981' }} />
                </Box>
            ) : error ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <Typography sx={{ color: '#F87171', mb: 2 }}>{error}</Typography>
                    <Button variant="outlined" onClick={fetchOrganisations}>Retry</Button>
                </Box>
            ) : filteredOrganisations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '16px' }}>
                    <BusinessIcon sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.15)', mb: 2 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', mb: 1 }}>No organisations found.</Typography>
                    <Chip
                        label="Try adjusting your search"
                        size="small"
                        sx={{ bgcolor: alpha('#10B981', 0.08), color: '#10B981', border: `1px solid ${alpha('#10B981', 0.2)}` }}
                    />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {paginatedOrganisations.map((organisation) => (
                        <Grid
                            key={organisation.org_id}
                            size={{ xs: 12, md: 6, lg: 4 }}
                        >
                            <OrganisationCard
                                organisation={organisation}
                                memberCount={memberCounts[organisation.org_id] ?? 0}
                                projectCount={projectCounts[organisation.org_id] ?? 0}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Pagination */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 6
                }}
            >
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.4)',
                        fontSize: '0.85rem'
                    }}
                >
                    Showing {paginatedOrganisations.length} of{' '}
                    {filteredOrganisations.length} organisations
                </Typography>

                <Pagination
                    page={page}
                    count={totalPages}
                    onChange={(_, value) => setPage(value)}
                    sx={{
                        '& .MuiPaginationItem-root': {
                            color: 'rgba(255,255,255,0.5)',
                            '&.Mui-selected': {
                                bgcolor: alpha('#10B981', 0.1),
                                color: '#10B981'
                            }
                        }
                    }}
                />
            </Box>

            <CreateOrganisationDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onCreated={fetchOrganisations}
            />
        </Box>
    );
};

export default OrganisationsPage;
